/// <reference types="vite-plugin-svgr/client" />
import { useState, useRef, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { FlexLayout, FlexChild } from 'xureact/lib/module/components/layout/flex-layout';

import { Layer } from './types/layer';
import { Whiteboard, WhiteboardRef } from './whiteboard';
import { LayerManager } from './layer-manager';
//import { ReactComponent as EraserSVG } from './eraser.svg';
import EraserSVG from './eraser.svg?react';

import './App.scss';

const rainbowColors = [
  '#ffffff',
  '#ff0000',
  '#ff7f00',
  '#ffff00',
  '#00ff00',
  '#23c8bd',
  '#5050ff',
  '#9900dd'
];

interface SublayerInfo {
  id: string;
  thumbnail: string;
  type: 'undo' | 'redo';
  index: number;
}

const App = () => {
  const [color, setColor] = useState<string>(rainbowColors[0]);
  const [mode, setMode] = useState<'pencil' | 'eraser'>('pencil');
  const [gridSize, setGridSize] = useState<number>(100);
  const [layers, setLayers] = useState<Layer[]>([{
    id: uuid(),
    label:'Background',
    visible: true
  }]);
  const [selectedLayerId, setSelectedLayerId] = useState<string>(layers[0].id);
  const [sublayers, setSublayers] = useState<SublayerInfo[]>([]);
  const whiteboardRefs = useRef<{ [key: string]: WhiteboardRef | null }>({});

  // Update sublayers when selected layer changes or when whiteboard refs are available
  useEffect(() => {
    const updateSublayers = () => {
      const selectedRef = whiteboardRefs.current[selectedLayerId];
      if (selectedRef) {
        const sublayerInfo = selectedRef.getSublayerInfo();
        const newSublayers: SublayerInfo[] = [];

        // Add undo stack items (in reverse order to show most recent first)
        sublayerInfo.undoStack.slice().reverse().forEach((thumbnail, index) => {
          newSublayers.push({
            id: `undo-${index}`,
            thumbnail,
            type: 'undo',
            index: sublayerInfo.undoStack.length - index
          });
        });

        // Add redo stack items
        sublayerInfo.redoStack.forEach((thumbnail, index) => {
          newSublayers.push({
            id: `redo-${index}`,
            thumbnail,
            type: 'redo',
            index: index + 1
          });
        });

        setSublayers(newSublayers);
      } else {
        setSublayers([]);
      }
    };

    // Small delay to ensure refs are available
    const timeoutId = setTimeout(updateSublayers, 100);
    return () => clearTimeout(timeoutId);
  }, [selectedLayerId]);

  // Force update sublayers periodically to catch undo/redo changes
  useEffect(() => {
    const intervalId = setInterval(() => {
      const selectedRef = whiteboardRefs.current[selectedLayerId];
      if (selectedRef) {
        const sublayerInfo = selectedRef.getSublayerInfo();
        const newSublayers: SublayerInfo[] = [];

        // Add undo stack items (in reverse order to show most recent first)
        sublayerInfo.undoStack.slice().reverse().forEach((thumbnail, index) => {
          newSublayers.push({
            id: `undo-${index}`,
            thumbnail,
            type: 'undo',
            index: sublayerInfo.undoStack.length - index
          });
        });

        // Add redo stack items
        sublayerInfo.redoStack.forEach((thumbnail, index) => {
          newSublayers.push({
            id: `redo-${index}`,
            thumbnail,
            type: 'redo',
            index: index + 1
          });
        });

        setSublayers(newSublayers);
      }
    }, 100); // Update every 100ms

    return () => clearInterval(intervalId);
  }, [selectedLayerId]);

  const handleColorChange = (newColor: string) => {
    setColor(newColor);
    setMode('pencil');
  };

  const handleModeChange = (newMode: 'pencil' | 'eraser') => {
    setMode(newMode);
  };

  const handleAddLayer = (layerId: string) => {
    const newLayers = [
      {
        id: layerId,
        label: `Layer #${layers.length}`,
        visible: true
      },
      ...layers
    ];
    setLayers(newLayers);
    setSelectedLayerId(newLayers[0].id);
  };

  const handleSelectLayer = (layerId: string) => {
    setSelectedLayerId(layerId);
  };

  const handleToggleLayerVisible = (layerId: string) => {
    const index = layers.findIndex((layer) => layer.id === layerId);
    if (index > -1) {
      const newLayers = [...layers];
      newLayers[index].visible = !newLayers[index].visible;
      setLayers(newLayers);
    }
  };

  const handleDeleteLayer = (layerId: string) => {
    const newLayers = layers.filter((layer) => layer.id !== layerId);
    setLayers(newLayers);
  };

  const handleMoveLayerUp = (layerId: string) => {
    const index = layers.findIndex((layer) => layer.id === layerId);
    if (index > 0) {
      const newLayers = [...layers];
      [newLayers[index], newLayers[index - 1]] = [newLayers[index - 1], newLayers[index]];
      setLayers(newLayers);
    }
  };

  const handleMoveLayerDown = (layerId: string) => {
    const index = layers.findIndex((layer) => layer.id === layerId);
    if (index < layers.length - 1) {
      const newLayers = [...layers];
      [newLayers[index], newLayers[index + 1]] = [newLayers[index + 1], newLayers[index]];
      setLayers(newLayers);
    }
  };

  const handleGenerateGrid = () => {
    const gridLayerId = uuid();
    const newLayers = [
      ...layers,
      {
        id: gridLayerId,
        label: `Grille ${gridSize}px`,
        visible: true
      }
    ];
    setLayers(newLayers);
    setSelectedLayerId(gridLayerId);

    // Dessiner la grille après un court délai pour s'assurer que le composant est monté
    setTimeout(() => {
      const gridRef = whiteboardRefs.current[gridLayerId];
      if (gridRef && 'drawGrid' in gridRef) {
        (gridRef as any).drawGrid(gridSize, color);
      }
    }, 100);
  };


  const handleActionPerformed = (layerId: string, imageData: ImageData) => {
    // This is now handled internally by each Whiteboard component
    console.log(`Action performed on layer ${layerId}`);
  };

  const layersReversed = [ ... layers ].reverse();

  return (
    <div className="fullh">
      <FlexLayout direction="column">
        <FlexChild grow={0}>
          <FlexLayout direction="row" className="header-bar">
            <FlexChild grow={0}>
              <div className="color-palette">
                {rainbowColors.map((c, index) => (
                  <div
                    key={index}
                    className={`color ${mode === 'pencil' && color === c ? 'active' : ''}`}
                    style={{ backgroundColor: c }}
                    onClick={() => handleColorChange(c)}
                  />
                ))}
                <button
                  className={`mode-button color ${mode === 'eraser' ? 'active' : ''}`}
                  onClick={() => handleModeChange('eraser')}
                >
                  <EraserSVG fill={mode === 'eraser' ? 'red' : '#fff'}/>
                </button>
              </div>
            </FlexChild>
            <FlexChild grow={0}>
              <div className="grid-controls">
                <button
                  className="grid-button"
                  onClick={handleGenerateGrid}
                >
                  Grille
                </button>
                <input
                  type="number"
                  value={gridSize}
                  onChange={(e) => setGridSize(parseInt(e.target.value) || 120)}
                  min="10"
                  max="500"
                  className="grid-size-input"
                  placeholder="120"
                />
                <span className="grid-size-label">px</span>
              </div>
            </FlexChild>
          </FlexLayout>
        </FlexChild>
        <FlexChild grow={1}>
          <FlexLayout direction="row">
            <FlexChild grow={1}>
              <div className="whiteboard-area">
                {layersReversed.map((layer) => (
                  <Whiteboard
                    key={layer.id}
                    color={color}
                    mode={mode}
                    isEditing={layer.id === selectedLayerId}
                    visible={layer.visible}
                    layerId={layer.id}
                    onActionPerformed={handleActionPerformed}
                    ref={(ref) => {
                      whiteboardRefs.current[layer.id] = ref;
                    }}
                  />
                ))}
              </div>
            </FlexChild>
            <FlexChild width={600}>
              <LayerManager
                layers={layers}
                selectedLayerId={selectedLayerId}
                sublayers={sublayers}
                onAddLayer={handleAddLayer}
                onSelectLayer={handleSelectLayer}
                onToggleLayerVisible={handleToggleLayerVisible}
                onDeleteLayer={handleDeleteLayer}
                onMoveLayerUp={handleMoveLayerUp}
                onMoveLayerDown={handleMoveLayerDown}
              />
            </FlexChild>
          </FlexLayout>
        </FlexChild>
      </FlexLayout>
    </div>
  );
};

export { App };
